import * as Icons from '../icons';
import { CustomTooltip, NoDataMessage, ValueOverlay } from './components';
import {
  areaConfig,
  chartMargins,
  chartStyles,
  getGradientStops,
  getGridStrokeColor,
  gradientConfig,
  gridConfig,
  responsiveContainerConfig,
  xAxisConfig,
  yAxisConfig,
} from './config/chartConfig';
import { defaultOrientationWarningMessage, defaultOrientationWarningTitle } from './constants';
import { useChartData, usePaintMode, usePowerLimits } from './hooks';
import { type PowerProfileChartProps } from './types';
import { calculateChartColor, createEditCursor, formatXAxisTick, calculateDailyTickInterval } from './utils';
import { Alert, AlertTitle, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRef } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export function PowerProfileChart({
  data = [],
  height = 300,
  title,
  color,
  peakPowerKw,
  averagePowerKw,
  workingDays,
  workingHoursStart,
  workingHoursEnd,
  orientationWarningTitle = defaultOrientationWarningTitle,
  orientationWarningMessage = defaultOrientationWarningMessage,
  noDataMessage = 'No data available',
  enablePaint = false,
  onDataPaint,
  xAxisTickFormatter,
  xAxisInterval,
  titleStyles,
}: PowerProfileChartProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const chartRef = useRef<HTMLDivElement>(null);

  // Chart data hook
  const { chartData } = useChartData({
    data,
    peakPowerKw,
    averagePowerKw,
    workingDays,
    workingHoursStart,
    workingHoursEnd,
  });

  // Chart color calculation
  const chartColor = calculateChartColor(color, theme.palette.primary.main);

  // Power limits hook
  const { dynamicPowerLimits, updateDynamicPowerLimits, resetLimits } = usePowerLimits({ chartData });

  // Paint mode hook
  const {
    isPainting,
    previewDataPoints,
    currentPaintValue,
    mousePosition,
    handleMouseDown: handlePaintMouseDown,
    handleMouseUp: handlePaintMouseUp,
  } = usePaintMode({
    enablePaint,
    chartData,
    chartRef,
    dynamicPowerLimits,
    onDataPaint,
    updateDynamicPowerLimits,
    onPaintEnd: resetLimits,
  });

  const cursorStyle = enablePaint ? createEditCursor(theme.palette.primary.main) : chartStyles.cursor.default;
  const chartTitleStyles = {
    fontSize: '14px',
    fontWeight: 600,
  };

  return (
    <Box data-testid="power-profile-chart">
      {title && (
        <Box sx={{ mb: 2 }}>
          {typeof title === 'string' ? (
            <Typography sx={{...chartTitleStyles, ...titleStyles}}>{title}</Typography>
          ) : (
            <Box sx={{...chartTitleStyles, ...titleStyles}}>{title}</Box>
          )}
        </Box>
      )}

      {chartData.length === 0 ? (
        <NoDataMessage message={noDataMessage} height={height} />
      ) : (
        <>
          <Box
            ref={chartRef}
            onMouseDown={(e) => {
              if (enablePaint) {
                handlePaintMouseDown(e);
              }
            }}
            onMouseUp={() => {
              if (enablePaint && isPainting) {
                handlePaintMouseUp();
              }
            }}
            sx={{
              position: 'relative',
              cursor: cursorStyle,
              userSelect: chartStyles.userSelect,
              '& *': {
                cursor: 'inherit !important',
              },
            }}
          >
            <ResponsiveContainer width={responsiveContainerConfig.width} height={height}>
              <AreaChart data={chartData} margin={chartMargins}>
                <defs>
                  <linearGradient id={gradientConfig.id} x1="0" y1="0" x2="0" y2="1">
                    {getGradientStops(theme).map((stop, index) => (
                      <stop key={index} offset={stop.offset} stopColor={stop.stopColor} />
                    ))}
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray={gridConfig.strokeDasharray}
                  stroke={getGridStrokeColor(theme)}
                  horizontal={gridConfig.horizontal}
                  vertical={gridConfig.vertical}
                />
                <XAxis
                  dataKey={xAxisConfig.dataKey}
                  tick={xAxisConfig.tick}
                  tickLine={xAxisConfig.tickLine}
                  axisLine={xAxisConfig.axisLine}
                  tickFormatter={xAxisTickFormatter || formatXAxisTick(chartData)}
                  interval={xAxisInterval !== undefined ? xAxisInterval : calculateDailyTickInterval(chartData)}
                />
                <YAxis
                  tick={yAxisConfig.tick}
                  tickLine={yAxisConfig.tickLine}
                  axisLine={yAxisConfig.axisLine}
                  width={80}
                  domain={
                    dynamicPowerLimits
                      ? [dynamicPowerLimits.minPower, dynamicPowerLimits.maxPower]
                      : ['dataMin', 'dataMax']
                  }
                  tickFormatter={(value) => `${yAxisConfig.tickFormatter(value)} kW`}
                />
                <Tooltip content={(props) => <CustomTooltip {...props} />} />
                <Area
                  type={areaConfig.type}
                  dataKey={areaConfig.dataKey}
                  stroke={chartColor}
                  fill={`url(#${gradientConfig.id})`}
                  fillOpacity={areaConfig.fillOpacity}
                  strokeWidth={areaConfig.strokeWidth}
                  activeDot={
                    isPainting
                      ? false
                      : {
                          ...areaConfig.activeDot,
                          stroke: chartColor,
                          cursor: 'default',
                        }
                  }
                  dot={areaConfig.dot}
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Preview line during painting - rendered as SVG overlay */}
            {isPainting && previewDataPoints && previewDataPoints.length > 0 && dynamicPowerLimits && (
              <svg
                style={{
                  position: 'absolute',
                  top: chartMargins.top,
                  left: 20 + 80, // marginLeft + yAxisWidth
                  right: chartMargins.right,
                  bottom: 0,
                  pointerEvents: 'none',
                  overflow: 'visible',
                }}
                width={`calc(100% - ${20 + 80 + chartMargins.right}px)`}
                height={height - chartMargins.top}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <polyline
                  points={previewDataPoints
                    .map((point) => {
                      const index = chartData.findIndex((p) => p.timestamp === point.timestamp);
                      if (index === -1) {
                        return null;
                      }

                      // Validate power value
                      if (typeof point.power !== 'number' || isNaN(point.power)) {
                        return null;
                      }

                      // Calculate X position (0 to 1 across chart width)
                      const xRatio = chartData.length > 1 ? index / (chartData.length - 1) : 0;

                      // Calculate Y position using ORIGINAL dynamicPowerLimits (same as main chart)
                      // This ensures SVG overlay aligns with the actual chart Y-axis
                      const powerRange = dynamicPowerLimits.maxPower - dynamicPowerLimits.minPower;
                      if (powerRange === 0) {
                        return null;
                      }

                      const yRatio = (dynamicPowerLimits.maxPower - point.power) / powerRange;

                      // Validate calculated ratios
                      if (isNaN(xRatio) || isNaN(yRatio)) {
                        return null;
                      }

                      // Scale to viewBox coordinates (0-100)
                      const x = xRatio * 100;
                      const y = yRatio * 100;

                      return `${x},${y}`;
                    })
                    .filter((p) => p !== null)
                    .join(' ')}
                  fill="none"
                  stroke="#ff0000"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            )}

            {isPainting && currentPaintValue !== null && (
              <ValueOverlay currentValue={currentPaintValue} mousePosition={mousePosition} />
            )}

            {/* Custom dot at cursor position during painting */}
            {isPainting && mousePosition && currentPaintValue !== null && dynamicPowerLimits && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  overflow: 'visible',
                }}
              >
                <circle
                  cx={mousePosition.x}
                  cy={mousePosition.y}
                  r={6}
                  fill={chartColor}
                  stroke="#fff"
                  strokeWidth={2}
                />
              </svg>
            )}
          </Box>
          {/* Feature: Mobile orientation warning
              When: Screen is small
              Then: Show orientation warning */}
          {isSmallScreen && (
            <Alert
              severity="info"
              variant="filled"
              icon={<Icons.ScreenRotation sx={{ alignSelf: 'center', fontSize: '36px' }} />}
              sx={{ mt: 2 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <AlertTitle sx={{ fontWeight: 600 }}>{orientationWarningTitle}</AlertTitle>
                {orientationWarningMessage}
              </Box>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}